import { Button, Card, Rate } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	compareStrings,
	getEntityById,
	isAdminUser,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import WorkOrderReviewComponent from '../WorkOrderReviewComponent';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	supplierFacilityReviewsRestCrudThunksForBuyer,
	supplierFacilityReviewsRestCrudThunksForSupplier,
} from '../../thunks/supplier_facility_reviews_thunks';
import { UserName } from '../name_component/user/UserNameComponent';
import DateTimeHover from '../date_time_component/DateTime';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import { WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import { withUserType } from '../withUserType';

function WorkOrderRating({ workOrder, fetchSupplierFacilityReviews, reviews, currentUser }) {
	const [feedbackId, setFeedbackId] = useState(null);
	const [fetchedFeedback, setFetchedFeedback] = useState(null);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [newRatingModalVisible, setNewRatingModalVisible] = useState(false);

	const workOrderId = nullSafeGet('id', workOrder);
	const supplierFacilityId = nullSafeGet('supplierFacilityId', workOrder);
	const workOrderStatus = nullSafeGet('status', workOrder);
	useEffect(() => {
		fetchSupplierFacilityReviews({
			workOrderId,
			supplierFacilityId,
		}).then((data) => {
			setFeedbackId(nullSafeGet(`${nullSafeGetOrElse('length', data, 0) - 1}.id`, data));
			setFetchedFeedback(data);
		});
	}, [workOrderStatus]);

	const isInternalTeamorBuyer = useMemo(() => isBuyerOrInternalTech(currentUser), [currentUser]);

	const canGiveFeedback = useMemo(() => {
		return (
			isInternalTeamorBuyer &&
			supplierFacilityId &&
			nullSafeGetOrElse('supplierFacility.status', workOrder, 'active') !== 'private' &&
			compareStrings(workOrder.displayStatus, WORK_ORDER_DISPLAY_STATUSES.complete)
		);
	}, [workOrder, isInternalTeamorBuyer, supplierFacilityId]);

	const feedback = getEntityById(feedbackId, reviews);
	const hasExistingFeedback = feedback && feedback.rating;

	if (!fetchedFeedback) return null;

	if (!hasExistingFeedback && !canGiveFeedback) return null;

	const { rating, reviewTags, numRatings } = feedback || {};
	const allowEdit =
		isInternalTeamorBuyer &&
		hasExistingFeedback &&
		(isAdminUser(currentUser) || nullSafeGet('buyerContactEmail', feedback) === currentUser.email);

	return (
		<div className="rowSpacing">
			<Card
				title={isInternalTeamorBuyer ? 'Service Provider Feedback' : 'Feedback'}
				extra={
					hasExistingFeedback
						? allowEdit && (
								<Button
									type="link"
									onClick={() => {
										setEditModalVisible(true);
									}}
								>
									Edit
								</Button>
						  )
						: null
				}
			>
				{hasExistingFeedback ? (
					<div className="mt-2 flex flex-col justify-center gap-4 ">
						<div className="flex flex-row justify-center gap-x-2 text-sm text-gray-400">
							<Rate style={{ fontSize: 40 }} disabled value={rating} allowHalf />
						</div>
						<div className="mt-2 flex flex-row flex-wrap justify-center gap-2">
							{reviewTags && reviewTags.length
								? reviewTags.map((tag) => {
										return (
											<div
												key={nullSafeGet('id', tag)}
												className={`relative inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
													nullSafeGet('isPositiveSentiment', tag)
														? `bg-green-50 text-green-700 ring-green-600/20`
														: `bg-red-50 text-red-700 ring-red-600/10`
												}`}
											>
												<span>{nullSafeGet('title', tag)} </span>
											</div>
										);
								  })
								: null}
						</div>
						{feedback.review ? (
							<div className="rounded-xl bg-slate-200 p-3 text-center text-base text-gray-500 ">
								"{feedback.review}"
							</div>
						) : null}
						<div className="gap-x-2 text-sm text-gray-400">
							Rated by <UserName contact={nullSafeGet('buyerContact', feedback)} mode="inline" />{' '}
							<DateTimeHover
								timestamp={nullSafeGet('createdAt', feedback)}
								timeZone={nullSafeGet('location.timeZoneId', workOrder)}
							/>
						</div>
					</div>
				) : (
					<div className="mt-2 flex flex-col justify-center gap-4 text-center">
						<div className="text-lg text-gray-500">
							{canGiveFeedback
								? "No feedback available. Click 'Rate Service' to provide your feedback."
								: null}
						</div>
						{canGiveFeedback ? (
							<Button
								type="primary"
								size="large"
								onClick={() => {
									setNewRatingModalVisible(true);
								}}
							>
								Rate Service
							</Button>
						) : null}
					</div>
				)}
			</Card>
			{editModalVisible ? (
				<WorkOrderReviewComponent
					formData={feedback}
					successCb={() => {
						setEditModalVisible(false);

						fetchSupplierFacilityReviews({
							workOrderId,
							supplierFacilityId,
						}).then((data) => {
							setFeedbackId(nullSafeGet(`${nullSafeGetOrElse('length', data, 0) - 1}.id`, data));
							setFetchedFeedback(data);
						});
					}}
					submitting={reviews.fetching}
					onCancel={() => setEditModalVisible(false)}
					workOrder={workOrder}
				/>
			) : null}
			{newRatingModalVisible ? (
				<WorkOrderReviewComponent
					formData={{}}
					successCb={() => {
						setNewRatingModalVisible(false);
						// Refresh the feedback data
						fetchSupplierFacilityReviews({
							workOrderId,
							supplierFacilityId,
						}).then((data) => {
							setFeedbackId(nullSafeGet(`${nullSafeGetOrElse('length', data, 0) - 1}.id`, data));
							setFetchedFeedback(data);
						});
					}}
					submitting={reviews.fetching}
					onCancel={() => setNewRatingModalVisible(false)}
					workOrder={workOrder}
				/>
			) : null}
		</div>
	);
}

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	reviews: state.supplier_facility_reviews,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchSupplierFacilityReviews: (
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilityReviewsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: supplierFacilityReviewsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	updateSupplierFacilityReview: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(supplierFacilityReviewsRestCrudThunksForSupplier.update(entity))
			: dispatch(supplierFacilityReviewsRestCrudThunksForBuyer.update(entity)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(WorkOrderRating));
